/**
 * Reorder-model editor (roadmap inventory Phase 3a). Edits the tunable reorder model — lead time,
 * service level, and the trailing window — that drives every suggested reorder point and draft-PO
 * quantity. Reached from the Short & reorder tab's "model:" line, where the effects show.
 *
 * Money-ADJACENT (the params drive purchasing dollars) but not a ledger write: the human still reviews
 * the draft PO + places every S&S order manually. WRITES are admin / inventory-admin only (server-
 * enforced via requireInventoryWrite); the form is disabled for a read-only viewer (cosmetic — the
 * server 403 is the real gate). Service level is a picker so `serviceZ` + its label never drift.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useApiClient } from '@/lib/api/hooks';
import { useAuth } from '@/lib/auth-state';
import { invalidateInventory } from './inventory-lib';

/** Common service levels → their normal-distribution z factor. The only way to set the model, so the
 *  stored `serviceZ`/`serviceLabel` are always one of these (or the '95%' default) — no drift. */
const SERVICE_LEVELS = [
  { label: '90%', z: 1.28 },
  { label: '95%', z: 1.65 },
  { label: '97.5%', z: 1.96 },
  { label: '99%', z: 2.33 },
] as const;

export function ReorderConfigDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md">
        {open && <ReorderConfigBody onClose={onClose} />}
      </DialogContent>
    </Dialog>
  );
}

function ReorderConfigBody({ onClose }: { onClose: () => void }) {
  const client = useApiClient();
  const qc = useQueryClient();
  const { state } = useAuth();
  const canWrite = state.status === 'signed-in' && state.role.isInventoryWrite;

  const query = useQuery({
    queryKey: ['reorder-config'],
    queryFn: async () => {
      const { data, error } = await client.GET('/reorder-config/current');
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load the reorder model.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const [lead, setLead] = useState('');
  const [window, setWindow] = useState('');
  const [level, setLevel] = useState('95%');

  // Seed (and re-seed on a refetch) from the server value so a save always reflects the latest saved
  // model rather than clobbering a concurrent edit with a stale form (the #117 par-clobber lesson).
  const cfg = query.data;
  useEffect(() => {
    if (!cfg) return;
    setLead(String(cfg.body.leadTimeDays));
    setWindow(String(cfg.body.windowDays));
    setLevel(cfg.body.serviceLabel);
  }, [cfg]);

  const leadN = Number(lead);
  const windowN = Number(window);
  const leadValid = lead.trim() !== '' && Number.isInteger(leadN) && leadN >= 1 && leadN <= 90;
  const windowValid =
    window.trim() !== '' && Number.isInteger(windowN) && windowN >= 1 && windowN <= 365;
  const chosen = SERVICE_LEVELS.find((l) => l.label === level) ?? SERVICE_LEVELS[1];
  const dirty =
    !!cfg &&
    (leadN !== cfg.body.leadTimeDays ||
      windowN !== cfg.body.windowDays ||
      level !== cfg.body.serviceLabel);

  const save = useMutation({
    mutationFn: async () => {
      const { data, error } = await client.POST('/reorder-config', {
        body: {
          leadTimeDays: leadN,
          windowDays: windowN,
          serviceZ: chosen.z,
          serviceLabel: chosen.label,
        },
      });
      if (error || !data?.ok) throw new Error(error?.error?.detail ?? 'Save failed.');
    },
    onSuccess: () => {
      // The saved model changes every suggested reorder point + draft-PO qty → refetch the whole family.
      qc.invalidateQueries({ queryKey: ['reorder-config'] });
      invalidateInventory(qc);
      onClose();
    },
  });

  const onDefaults = cfg?.source === 'fallback-unseeded';

  return (
    <>
      <DialogHeader>
        <DialogTitle>Reorder model</DialogTitle>
        <DialogDescription>
          The inputs behind every suggested reorder level and draft-PO quantity. Changing them
          re-computes the Short &amp; reorder view — it never places an order.
        </DialogDescription>
      </DialogHeader>

      {query.isPending && (
        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      )}

      {query.isError && (
        <p className="text-sm text-destructive">
          {query.error instanceof Error ? query.error.message : 'Failed to load.'}
        </p>
      )}

      {cfg && (
        <div className="space-y-4">
          {onDefaults && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              Using the built-in defaults.
            </p>
          )}
          {!canWrite && (
            <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              Read-only view. Editing the reorder model requires the admin or inventory-admin group.
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="ro-lead" className="text-xs">
              Lead time (days)
            </Label>
            <Input
              id="ro-lead"
              type="number"
              inputMode="numeric"
              min="1"
              max="90"
              step="1"
              value={lead}
              disabled={!canWrite}
              onChange={(e) => setLead(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              How long a restock takes to arrive. 1–90.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ro-service" className="text-xs">
              Service level
            </Label>
            <Select value={level} onValueChange={setLevel} disabled={!canWrite}>
              <SelectTrigger id="ro-service">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_LEVELS.map((l) => (
                  <SelectItem key={l.label} value={l.label}>
                    {l.label} service (z {l.z})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">
              Higher = a bigger safety cushion against a stockout (and more stock held).
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="ro-window" className="text-xs">
              History window (days)
            </Label>
            <Input
              id="ro-window"
              type="number"
              inputMode="numeric"
              min="1"
              max="365"
              step="1"
              value={window}
              disabled={!canWrite}
              onChange={(e) => setWindow(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              How far back consumption is averaged. 1–365.
            </p>
          </div>

          {save.isError && (
            <p className="text-xs text-destructive">
              {save.error instanceof Error ? save.error.message : 'Save failed.'}
            </p>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={() => save.mutate()}
              disabled={!canWrite || !leadValid || !windowValid || !dirty || save.isPending}
            >
              {save.isPending && <Loader2 className="size-4 animate-spin" />}
              Save model
            </Button>
          </div>
        </div>
      )}
    </>
  );
}
